import { Injectable } from '@angular/core';

import { pmStorageGet } from '../utils/pm-storage.util';
const DEFAULT_PRIMARY_BRAND_COLOR = '#694baf';
const PRIMARY_COLOR_STORAGE_KEY = 'capium-primary-brand-color';
const PRIMARY_LIGHT_WHITE_FRACTION = 0.82;

@Injectable({ providedIn: 'root' })
export class PmBrandThemeService {
  restorePrimaryBrandColor(): void {
    let stored: string | null = null;
    try {
      stored = pmStorageGet(PRIMARY_COLOR_STORAGE_KEY);
    } catch {
      stored = null;
    }

    this.applyPrimaryBrandColor(this.normalizeHexColor(stored ?? '') ?? DEFAULT_PRIMARY_BRAND_COLOR);
  }

  applyPrimaryBrandColor(hex: string): void {
    if (typeof document === 'undefined') {
      return;
    }
    const normalized = this.normalizeHexColor(hex) ?? DEFAULT_PRIMARY_BRAND_COLOR;
    const root = document.documentElement;
    root.style.setProperty('--capium-purple', normalized);
    root.style.setProperty('--capium-purple-dark', this.shadeHexColor(normalized, -0.18));
    root.style.setProperty('--capium-purple-light', this.mixTowardWhite(normalized, PRIMARY_LIGHT_WHITE_FRACTION));
    // DS-16: the --ds-color-primary-* scale (tokens.css) shifts with the custom brand colour too,
    // so a firm that sets one stays consistent once styling migrates onto the --ds- tokens.
    // -light's own 0.82 white-mix doesn't line up with any of the 50/100/300/500 steps, so it gets
    // its own alias instead of being forced onto one of them (that would shift the visible colour).
    root.style.setProperty('--ds-color-primary-50', this.mixTowardWhite(normalized, 0.95));
    root.style.setProperty('--ds-color-primary-100', this.mixTowardWhite(normalized, 0.9));
    root.style.setProperty('--ds-color-primary-300', this.mixTowardWhite(normalized, 0.55));
    root.style.setProperty('--ds-color-primary-500', this.mixTowardWhite(normalized, 0.2));
    root.style.setProperty('--ds-color-primary-600', normalized);
    root.style.setProperty('--ds-color-primary-700', this.shadeHexColor(normalized, -0.18));
    root.style.setProperty('--ds-color-primary-light', this.mixTowardWhite(normalized, PRIMARY_LIGHT_WHITE_FRACTION));
  }

  private normalizeHexColor(value: string): string | null {
    const v = value.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      return v.toLowerCase();
    }
    if (/^#[0-9a-fA-F]{3}$/.test(v)) {
      const r = v[1];
      const g = v[2];
      const b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    return null;
  }

  private mixTowardWhite(hex: string, whiteFraction: number): string {
    const n = parseInt(hex.slice(1), 16);
    const t = Math.min(1, Math.max(0, whiteFraction));
    const r = Math.round(((n >> 16) & 255) * (1 - t) + 255 * t);
    const g = Math.round(((n >> 8) & 255) * (1 - t) + 255 * t);
    const b = Math.round((n & 255) * (1 - t) + 255 * t);
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  }

  private shadeHexColor(hex: string, amount: number): string {
    const n = parseInt(hex.slice(1), 16);
    let r = (n >> 16) & 255;
    let g = (n >> 8) & 255;
    let b = n & 255;
    if (amount < 0) {
      const f = 1 + amount;
      r = Math.round(Math.max(0, Math.min(255, r * f)));
      g = Math.round(Math.max(0, Math.min(255, g * f)));
      b = Math.round(Math.max(0, Math.min(255, b * f)));
    } else {
      r = Math.round(Math.max(0, Math.min(255, r + (255 - r) * amount)));
      g = Math.round(Math.max(0, Math.min(255, g + (255 - g) * amount)));
      b = Math.round(Math.max(0, Math.min(255, b + (255 - b) * amount)));
    }
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  }
}
